/**
 * Hook for managing dashboard data loading
 * Progressively fetches lightweight users based on selected linked service
 */

import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchLightweightUsersV4,
  fetchGroups,
  fetchLinkedServices,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  selectGroupsWithDefaults,
  selectGroupsStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'

export function useDashboardData(
  linkedServiceID: string,
  onLinkedServiceAutoSelect?: (serviceID: string) => void,
) {
  let dispatch = useAppDispatch()

  // Selectors for data
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let groups = useAppSelector(selectGroupsWithDefaults(linkedServiceID || ''))
  let groupsStatus = useAppSelector(selectGroupsStatus)

  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(linkedServiceID || ''),
  )
  let lightweightUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(linkedServiceID || ''),
  )

  // Fetch linked services if not loaded
  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus, dispatch])

  // Fetch groups if not loaded
  useEffect(() => {
    if (groupsStatus === 'uninitialized') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus, dispatch])

  // Auto-select first linked service if none selected and services are available
  useEffect(() => {
    if (
      !linkedServiceID &&
      linkedServicesStatus === 'fulfilled' &&
      linkedServices.length > 0 &&
      onLinkedServiceAutoSelect
    ) {
      onLinkedServiceAutoSelect(linkedServices[0].id)
    }
  }, [
    linkedServiceID,
    linkedServicesStatus,
    linkedServices,
    onLinkedServiceAutoSelect,
  ])

  // Fetch lightweight users when linked service is selected
  useEffect(() => {
    if (linkedServiceID && lightweightUsersStatus === 'uninitialized') {
      dispatch(fetchLightweightUsersV4(linkedServiceID))
    }
  }, [linkedServiceID, lightweightUsersStatus, dispatch])

  // Determine overall loading state
  let isLoading =
    linkedServicesStatus === 'pending' ||
    groupsStatus === 'pending' ||
    (linkedServiceID && lightweightUsersStatus === 'pending')

  let hasRequiredData =
    linkedServicesStatus === 'fulfilled' &&
    groupsStatus === 'fulfilled' &&
    (!linkedServiceID || lightweightUsersStatus === 'fulfilled')

  return {
    linkedServices,
    linkedServicesStatus,
    groups,
    groupsStatus,
    lightweightUsers,
    lightweightUsersStatus,
    isLoading,
    hasRequiredData,
  }
}
